import React from "react";
import {createUseStyles, useTheme} from "react-jss";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {ExpansionTimeMS, ViewMyDashboardWidth, PanelState} from "./accessoryConstants";
import clsx from "clsx";
import {Theme} from "engage-client/theme";
import Button from "engage-client/components/Button";

const DecorativeLineWidth = 16;

const useStyles = createUseStyles<Theme>((theme) => ({
    expandContainer: {
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        width: ViewMyDashboardWidth,
        display: "flex",
        alignItems: "center",
    },
    button: {
        position: "relative",
        width: ViewMyDashboardWidth + 2,
        cursor: "pointer",
        right: ViewMyDashboardWidth - 1,
        height: 140,
        background: theme.accessoryPanel.expandButton.background ?? theme.accessoryPanel.background,
        border: "none",
        boxShadow: "none",
        borderRadius: 0,
        borderTopLeftRadius: 7,
        borderBottomLeftRadius: 7
    },
    buttonTab: {
        position: "absolute",
        right: -ViewMyDashboardWidth,
        top: "50%"
    },
    chevrons: {
        position: "absolute",
        width: 16,
        height: 14,
        right: ViewMyDashboardWidth + 8,
        top: "calc(50% + 44px)",
        transition: `transform ${ExpansionTimeMS}ms linear`,
        transform: ({panelState:ps}) => ps === PanelState.expanded || ps === PanelState.expanding ? "rotate(180deg)" : "rotate(0deg)"
    },
    "@keyframes fadeIn": {
        from: {
            opacity: 0
        }
    },
    glow: {
        animation: "$fadeIn 1s infinite alternate"
    },
    label: {
        position: "absolute",
        right: ViewMyDashboardWidth + 8,
        top: "calc(50% - 9px)",
        transition: `opacity 250ms linear, transform ${ExpansionTimeMS}ms linear`,
        fontWeight: 700,
        whiteSpace: "nowrap",
        writingMode: "vertical-rl",
        transform: "rotate(-180deg) translate(0, 50%)",
        fontSize: 16,
        opacity: 0,
        "&.show": {
            opacity: 1
        }
    },
    activeQuestionLabel: {
        position: "absolute",
        right: ViewMyDashboardWidth + 10,
        top: "calc(50% - 10px)",
        transition: "opacity 250ms linear",
        fontWeight: 700,
        whiteSpace: "nowrap",
        writingMode: "vertical-rl",
        transform: "rotate(-180deg) translate(0, 50%)",
        fontSize: 16,
        opacity: 0,
        "&.show": {
            opacity: 1
        }
    },
    lineContainer: {
        position: "absolute",
        bottom: 0,
        left: (ViewMyDashboardWidth - DecorativeLineWidth) / 2,
        opacity: 0.25
    },
}));

export type ExpandButtonProps = {
    toggleSlide?:()=>void
    panelState: PanelState
    showAnswerWarning: boolean
}

export const ExpandButton = ({toggleSlide, panelState, showAnswerWarning}:ExpandButtonProps) => {
    const theme:Theme = useTheme();
    const classes = useStyles({panelState});

    const lineHeight = 30;
    const lines = [];
    for(let i = 0; i <= lineHeight; i+=3) {
        lines.push(<line x1={0} x2={DecorativeLineWidth} y1={i} y2={i} strokeWidth={1} stroke="white" key={i}/>);
    }

    return (
        <div className={classes.expandContainer}>
            <div className={classes.button} onClick={toggleSlide}>
                <div className={classes.buttonTab}>
                    <img src={theme.accessoryPanel.expandButton.desktop.chevron.src}
                        srcSet={theme.accessoryPanel.expandButton.desktop.chevron.srcSet}
                        className={classes.chevrons}
                        alt="Expand or collapse dashboard"/>
                    {showAnswerWarning &&
                        <img src={theme.accessoryPanel.expandButton.desktop.chevronWarning.src}
                            srcSet={theme.accessoryPanel.expandButton.desktop.chevronWarning.srcSet}
                            className={clsx(classes.chevrons, classes.glow)}
                            alt="Expand or collapse dashboard"/>
                    }
                    <DisplayText variant={TextVariant.s14med} className={clsx(classes.label, !showAnswerWarning && "show")} color="white">
                        Dashboard
                    </DisplayText>
                    <DisplayText variant={TextVariant.s14med} className={clsx(classes.activeQuestionLabel, showAnswerWarning && "show")} color="white">
                        Answer Now
                    </DisplayText>
                </div>
            </div>
            <div className={classes.lineContainer}>
                <svg width={DecorativeLineWidth} height={lineHeight}>
                    {lines}
                </svg>
            </div>
        </div>
    );
};